import { ListSourceType } from "@/enum/list.enum";

import { ListOutreachConfig } from "../../schema/list-outreach-config.schema";
import { List } from "../../schema/list";

/**
 * Whether campaigns are usable on a list (PREC-3687 §4.2 / D5).
 *
 * The stored config decides once it has loaded. Until then — the config is fetched per list and
 * arrives after the first render — the list's **source-type default** applies, mirroring the API's
 * `defaultsForSourceType`: the Profile Tracker starts with campaigns off, every other type on. That
 * keeps a tracker from flashing campaign actions it does not offer.
 */
export const listCampaignsEnabled = (
	sourceType: ListSourceType | undefined,
	config: ListOutreachConfig | undefined
): boolean => config?.campaignsEnabled ?? sourceType !== ListSourceType.TRACKER;

/**
 * The other half of the same rule (PREC-3687 §4.4): whether the list auto-drafts chat copy.
 *
 * It is the exact inverse of `listCampaignsEnabled` — the Profile Tracker starts with chat on and
 * campaigns off, every other type the other way round — and it needs the same pre-config fallback
 * for the same reason: without it a tracker would flash "off" for one render before its stored
 * config lands. **Both mirror the API's `defaultsForSourceType`; a change to §4.4 touches both.**
 */
export const listAutoCreateAiCopy = (
	sourceType: ListSourceType | string | undefined,
	config: ListOutreachConfig | undefined
): boolean => config?.autoCreateAiCopy ?? sourceType === ListSourceType.TRACKER;

/**
 * The Reach out menu's gate for its two campaign paths ("Add to campaign" / "Review & edit
 * campaign"). Its own module so `ReachOutMenu.tsx` (434 lines, no ratchet headroom) only consumes
 * the answer, and so every combination is table-testable without rendering the menu.
 */
export const reachOutCampaignsEnabled = (
	list: List | undefined,
	config: ListOutreachConfig | undefined
): boolean => listCampaignsEnabled(list?.sourceType, config);
